import {
  WorkflowTriggerType,
  ChaseNotificationStatus,
} from '../../types/connector.types';
import type {
  ITimesheetConnector,
  TimeEntryFilters,
  BatchSaveResult,
  WorkflowTriggerPayload,
  OrderWorkflowTriggerPayload,
  PeopleFilters,
  PersonData,
  AssetData,
  ProjectData,
  BillOutRateData,
  BillOutRateFilters,
  ChaseNotificationPayload,
  ChaseNotification,
  ChaseNotificationFilters,
  CostTypeItem,
  WorkflowTriggerNotification,
  OrderWorkflowTriggerNotification,
  NotificationFilters,
} from '../../types/connector.types';
import {
  OrderStatus,
  OrderCostAllocation,
  OrderCurrency,
  OrderPriority,
  ApprovalStage,
  ApprovalDecision,
} from '../../types/ordering.types';
import type {
  Order,
  OrderLineItem,
  OrderApprovalLog,
  OrderComment,
  COSHHEntry,
  OrderAttachment,
  AttachmentCategory,
  OrderFilters,
  NewOrderData,
  OrderData,
  NewLineItemData,
  NewApprovalData,
  NewCommentData,
} from '../../types/ordering.types';
import type {
  TimeEntry,
  NewTimeEntry,
  Person,
  PersonOption,
  Project,
  BankHoliday,
  Asset,
  BillOutRate,
  ISODateString,
  PeriodMonth,
  TimeEntryAuditRecord,
} from '../../types/timesheet.types';
import {
  TimesheetStatus,
  ProjectType,
  CostType,
  PersonRole,
  BankHolidayRegion,
  ProjectStatus,
  toISODateString,
  toPeriodMonth,
} from '../../types/timesheet.types';
import { withRetry, pooledAll, SharePointError } from '../../utils/retry';
import { BATCH_CONCURRENCY, SP_LISTS, SP_PAGE_SIZE } from '../../utils/constants';
import { formatISODate } from '../../utils/dateUtils';
import { isNewEntry } from '../../types/timesheet.types';

// ─── Internal SP list item shape (untyped from API) ──────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SPRaw = Record<string, any>;

// ─── Workflow trigger action labels (used to build the Title field) ───────────

const TRIGGER_ACTION_LABELS: Record<WorkflowTriggerType, string> = {
  [WorkflowTriggerType.SubmitMonth]:      'Submit',
  [WorkflowTriggerType.ApproveMonth]:     'Manager Approve',
  [WorkflowTriggerType.RejectMonth]:      'Reject',
  [WorkflowTriggerType.SuperApproveMonth]: 'Super Approve',
  [WorkflowTriggerType.LockMonth]:        'Lock',
  [WorkflowTriggerType.UnlockMonth]:      'Unlock',
  [WorkflowTriggerType.SendChaseEmail]:   'Chase Email',
};

// ─── Standard non-project entries (always available to every user) ───────────
// These are not stored in the PROJECTS list — they're synthesized here so that
// Annual Leave, Sick Leave and G&A always appear in the timesheet dropdown and
// flow through the same grid/lookup code path as real projects.

const EPOCH_DATE = toISODateString('2000-01-01');
const virtualProject = (
  idSuffix: string,
  code: string,
  name: string,
  type: ProjectType,
): Project => ({
  id: `virtual-${idSuffix}` as Project['id'],
  projectCode: code,
  projectName: name,
  type,
  costCentre: '',
  projectOwnerName: '',
  projectOwnerEmail: '',
  assignedPMName: '',
  assignedPMEmail: '',
  startDate: EPOCH_DATE,
  endDate: null,
  estimatedHours: null,
  assignedTeamEmails: [],
  assignedTeamNames: [],
  isResearchDevelopment: false,
  status: ProjectStatus.Active,
  description: '',
});

const STANDARD_NON_PROJECT_ENTRIES: readonly Project[] = [
  virtualProject('annual-leave', 'ANNUAL-LEAVE', 'Annual Leave', ProjectType.Leave),
  virtualProject('sick-leave',   'SICK-LEAVE',   'Sick Leave',   ProjectType.Leave),
  virtualProject('ga',           'G&A',          'G&A',          ProjectType.GA),
];

// ─── Config ───────────────────────────────────────────────────────────────────

export interface SharePointConfig {
  siteUrl: string;
  /** Returns a valid Bearer token. Called before each request. */
  getAccessToken: () => Promise<string>;
}

// ─── Connector ────────────────────────────────────────────────────────────────

export class SharePointConnector implements ITimesheetConnector {
  private readonly baseUrl: string;
  private readonly getToken: () => Promise<string>;
  private readonly _spUserIdCache = new Map<string, number>();

  constructor({ siteUrl, getAccessToken }: SharePointConfig) {
    this.baseUrl = siteUrl.replace(/\/$/, '');
    this.getToken = getAccessToken;
  }

  // ── HTTP primitives ─────────────────────────────────────────────────────────

  private async fetch<T>(
    path: string,
    init: RequestInit = {}
  ): Promise<T> {
    const token = await this.getToken();
    const url = `${this.baseUrl}/_api${path}`;

    const response = await globalThis.fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json;odata=nometadata',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init.headers as Record<string, string>),
      },
    });

    if (response.status === 429 || response.status === 503) {
      throw new SharePointError(
        `SharePoint throttled (${response.status})`,
        response.status
      );
    }

    if (!response.ok) {
      const body = await response.text();
      throw new SharePointError(
        `SharePoint ${response.status} on ${path}`,
        response.status,
        body
      );
    }

    if (response.status === 204) return undefined as unknown as T;
    const text = await response.text();
    if (!text) return undefined as unknown as T;
    return JSON.parse(text) as T;
  }

  private listItems(listName: string) {
    return `/web/lists/getbytitle('${encodeURIComponent(listName)}')/items`;
  }

  private listItem(listName: string, id: string) {
    return `/web/lists/getbytitle('${encodeURIComponent(listName)}')/items(${id})`;
  }

  // ── Time Entries ─────────────────────────────────────────────────────────────

  async getTimeEntries(filters: TimeEntryFilters, listName: string = SP_LISTS.TIME_ENTRIES): Promise<TimeEntry[]> {
    const clauses: string[] = [];
    if (filters.personEmail) clauses.push(`PersonEmail eq '${filters.personEmail}'`);
    if (filters.periodMonth) {
      const [year, month] = filters.periodMonth.split('-').map(Number);
      const firstDay = `${filters.periodMonth}-01`;
      const lastDay = `${filters.periodMonth}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
      clauses.push(`DateWorked ge datetime'${firstDay}T00:00:00' and DateWorked le datetime'${lastDay}T23:59:59'`);
    }
    if (filters.status) clauses.push(`Status eq '${filters.status}'`);
    if (filters.managerEmail) clauses.push(`ManagerEmail eq '${filters.managerEmail}'`);
    if (filters.dateFrom) clauses.push(`DateWorked ge datetime'${filters.dateFrom}T00:00:00'`);
    if (filters.dateTo) clauses.push(`DateWorked le datetime'${filters.dateTo}T23:59:59'`);

    const filterStr = clauses.length > 0 ? `$filter=${clauses.join(' and ')}` : '';
    const selectStr =
      '$select=Id,PersonEmail,PersonName,ProjectCode,DateWorked,PeriodMonth,Hours,Status,' +
      'ManagerName,ManagerEmail,ManagerId,ManagerApprovedDate,' +
      'SuperApproverName,SuperApproverEmail,SuperApproverId,SuperApprovedDate,' +
      'SubmittedDate,LockedDate,UnlockedDate,UnlockReason,UnlockApprovedBy,' +
      'RejectionNote,BillRate,CostRate,TaskDescription,BillableToClient,' +
      'AssetCode,Created,Modified';

    const qs = [filterStr, selectStr, '$orderby=DateWorked asc,Id asc', `$top=${SP_PAGE_SIZE}`]
      .filter(Boolean)
      .join('&');

    const allItems: SPRaw[] = [];
    let nextPath: string | null = `${this.listItems(listName)}?${qs}`;

    while (nextPath) {
      const result = await withRetry(() =>
        this.fetch<{ value: SPRaw[]; 'odata.nextLink'?: string }>(nextPath!)
      );
      allItems.push(...result.value);
      const nextLink = result['odata.nextLink'];
      if (nextLink) {
        const apiIdx = nextLink.indexOf('/_api');
        nextPath = apiIdx >= 0 ? nextLink.slice(apiIdx + 5) : null;
      } else {
        nextPath = null;
      }
    }

    return allItems.map(mapTimeEntry);
  }

  async createTimeEntry(entry: NewTimeEntry, listName: string = SP_LISTS.TIME_ENTRIES): Promise<TimeEntry> {
    const resolved = await this.resolvePersonAndApproverIds(entry);
    const body = buildTimeEntryBody(resolved);
    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(listName), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapTimeEntry(result);
  }

  async updateTimeEntry(id: string, updates: Partial<TimeEntry>, listName: string = SP_LISTS.TIME_ENTRIES): Promise<void> {
    const resolved = await this.resolvePersonAndApproverIds(updates);
    const body = buildTimeEntryBody(resolved);
    await withRetry(() =>
      this.fetch(this.listItem(listName, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  async batchUpdateEntries(
    updates: Array<{ id: string; fields: Partial<TimeEntry> }>,
    listName: string = SP_LISTS.TIME_ENTRIES
  ): Promise<void> {
    const failed: string[] = [];
    const tasks = updates.map(({ id, fields }) => async () => {
      try {
        await this.updateTimeEntry(id, fields, listName);
      } catch {
        failed.push(id);
      }
    });
    await pooledAll(tasks, BATCH_CONCURRENCY);
    if (failed.length > 0) {
      throw new Error(`${failed.length} of ${updates.length} entr${failed.length === 1 ? 'y' : 'ies'} failed to update. Please try again.`);
    }
  }

  async batchSaveEntries(
    entries: Array<TimeEntry | NewTimeEntry>,
    listName: string = SP_LISTS.TIME_ENTRIES
  ): Promise<BatchSaveResult> {
    // Pre-warm the SP user ID cache for unique approver emails to avoid
    // concurrent duplicate calls when the same manager/super approver
    // appears across many entries.
    const uniqueEmails = new Set<string>();
    for (const e of entries) {
      if (e.managerEmail && e.managerId == null) uniqueEmails.add(e.managerEmail);
      if (e.superApproverEmail && e.superApproverId == null) uniqueEmails.add(e.superApproverEmail);
    }
    if (uniqueEmails.size > 0) {
      await Promise.all([...uniqueEmails].map(email => this.cachedEnsureUser(email).catch(() => {})));
    }

    const succeeded: string[] = [];
    const failed: BatchSaveResult['failed'] = [];
    const created: TimeEntry[] = [];

    const tasks = entries.map(entry => async () => {
      try {
        if (isNewEntry(entry)) {
          const saved = await this.createTimeEntry(entry, listName);
          succeeded.push(saved.id);
          created.push(saved);
        } else {
          await this.updateTimeEntry(entry.id, entry, listName);
          succeeded.push(entry.id);
        }
      } catch (err) {
        failed.push({
          id: entry.id,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });

    await pooledAll(tasks, BATCH_CONCURRENCY);
    return { succeeded, failed, created };
  }

  async deleteTimeEntries(
    ids: string[],
    listName: string = SP_LISTS.TIME_ENTRIES
  ): Promise<void> {
    const persistedIds = ids.filter(id => !id.startsWith('new-'));
    if (persistedIds.length === 0) return;
    const tasks = persistedIds.map(id => async () => {
      await withRetry(() =>
        this.fetch(this.listItem(listName, id), {
          method: 'DELETE',
          headers: { 'If-Match': '*' } as Record<string, string>,
        })
      );
    });
    await pooledAll(tasks, BATCH_CONCURRENCY);
  }

  // ── Audit Records ──────────────────────────────────────────────────────────────

  async batchSaveAuditRecords(records: TimeEntryAuditRecord[]): Promise<BatchSaveResult> {
    const succeeded: string[] = [];
    const failed: BatchSaveResult['failed'] = [];

    const tasks = records.map(record => async () => {
      try {
        const body = buildAuditRecordBody(record);
        const result = await withRetry(() =>
          this.fetch<SPRaw>(this.listItems(SP_LISTS.TIMESHEET_UNLOCK_AUDIT), {
            method: 'POST',
            body: JSON.stringify(body),
          })
        );
        succeeded.push(result.Id ?? 'unknown');
      } catch (err) {
        failed.push({
          id: `audit-${record.personEmail}-${record.periodMonth}`,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });

    await pooledAll(tasks, BATCH_CONCURRENCY);
    return { succeeded, failed };
  }

  // ── Workflow trigger (via SP list — no webhook URLs exposed) ────────────────

  async triggerWorkflow(payload: WorkflowTriggerPayload): Promise<void> {
    const actionLabel = TRIGGER_ACTION_LABELS[payload.triggerType] ?? payload.triggerType;
    const title = `${actionLabel} - ${payload.periodMonth} - ${payload.userName}`;

    const [userId, approverId] = await Promise.all([
      payload.userEmail
        ? this.cachedEnsureUser(payload.userEmail).catch(() => null)
        : Promise.resolve(null),
      payload.approverEmail
        ? this.cachedEnsureUser(payload.approverEmail).catch(() => null)
        : Promise.resolve(null),
    ]);

    const body: Record<string, unknown> = {
      Title: title,
      ActionType: payload.triggerType,
      Status: payload.actionStatus,
      PeriodMonth: payload.periodMonth,
      Timestamp: new Date().toISOString(),
      ApproverRole: payload.approverRole,
      ApproverName: payload.approverName,
      ApproverEmail: payload.approverEmail,
      UserName: payload.userName,
      UserEmail: payload.userEmail,
      Reason: payload.reason ?? '',
      RejectionComment: payload.rejectionComment ?? '',
      Source: payload.source,
      IsFinalState: payload.isFinalState,
    };

    if (userId != null) body.UserId = userId;
    if (approverId != null) body.ApproverId = approverId;
    if (payload.totalHours != null) body.TotalHours = payload.totalHours;
    if (payload.weekSummary != null) body.WeekSummary = payload.weekSummary;

    await withRetry(() =>
      this.fetch(this.listItems(SP_LISTS.WORKFLOW_TRIGGERS), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
  }

  // ── Order Workflow Trigger (via SP list — no webhook URLs exposed) ──────────

  async triggerOrderWorkflow(payload: OrderWorkflowTriggerPayload): Promise<void> {
    const title = `${payload.triggerType} - ${payload.orderRef}`;
    const body: Record<string, unknown> = {
      Title: title,
      TriggerType: payload.triggerType,
      OrderRef: payload.orderRef,
      OrderId: payload.orderId,
      RecipientEmail: payload.recipientEmail,
      RecipientName: payload.recipientName,
      RequesterEmail: payload.requesterEmail,
      RequesterName: payload.requesterName,
      OrderTitle: payload.orderTitle,
      OrderAmount: payload.orderAmount,
      TemplateName: payload.templateName,
      Status: 'Pending',
      Timestamp: new Date().toISOString(),
    };
    if (payload.note) body.Note = payload.note;
    if (payload.comment) body.Comments = payload.comment;
    if (payload.projectName) body.ProjectName = payload.projectName;

    try {
      await withRetry(() =>
        this.fetch(this.listItems(SP_LISTS.ORDER_WORKFLOW_TRIGGERS), {
          method: 'POST',
          body: JSON.stringify(body),
        })
      );
    } catch (err) {
      console.error('[OrderWorkflow] Failed to write trigger entry:', payload.triggerType, payload.orderRef, err);
      throw err;
    }
  }

  // ── Chase Notifications (audit + PowerAutomate trigger) ────────────────────

  async sendChaseNotification(payload: ChaseNotificationPayload): Promise<ChaseNotification> {
    const title = `${payload.personName} – ${payload.periodMonth}`;

    const personSpId = payload.personEmail
      ? await this.cachedEnsureUser(payload.personEmail).catch(() => null)
      : null;

    const body: Record<string, unknown> = {
      Title: title,
      PersonName: payload.personName,
      PersonEmail: payload.personEmail,
      PeriodMonth: payload.periodMonth,
      Status: ChaseNotificationStatus.Pending,
    };
    if (personSpId != null) body.PersonId = personSpId;

    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.CHASE_NOTIFICATIONS), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapChaseNotification(result);
  }

  async getChaseNotifications(filters: ChaseNotificationFilters = {}): Promise<ChaseNotification[]> {
    const clauses: string[] = [];
    if (filters.personEmail) clauses.push(`PersonEmail eq '${filters.personEmail}'`);
    if (filters.periodMonth) clauses.push(`PeriodMonth eq '${filters.periodMonth}'`);
    if (filters.status) clauses.push(`Status eq '${filters.status}'`);
    if (filters.since) clauses.push(`Created ge datetime'${filters.since}'`);

    const filterStr = clauses.length > 0 ? `$filter=${clauses.join(' and ')}&` : '';
    const qs = `${filterStr}$orderby=Created desc&$top=${SP_PAGE_SIZE}`;

    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.CHASE_NOTIFICATIONS)}?${qs}`)
    );
    return result.value.map(mapChaseNotification);
  }

  // ── Notification reads + hide ────────────────────────────────────────────────

  async getWorkflowTriggerNotifications(filters: NotificationFilters = {}): Promise<WorkflowTriggerNotification[]> {
    const clauses: string[] = ['HideNotification ne true'];
    if (filters.userEmail) {
      const e = filters.userEmail.replace(/'/g, "''");
      clauses.push(`(UserEmail eq '${e}' or ApproverEmail eq '${e}')`);
    }
    if (filters.since) clauses.push(`Created ge datetime'${filters.since}'`);
    const top = filters.limit ?? 200;
    const qs = `$filter=${clauses.join(' and ')}&$orderby=Created desc&$top=${top}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.WORKFLOW_TRIGGERS)}?${qs}`)
    );
    return result.value.map(mapWorkflowTriggerNotification);
  }

  async getOrderWorkflowTriggerNotifications(filters: NotificationFilters = {}): Promise<OrderWorkflowTriggerNotification[]> {
    const clauses: string[] = ['HideNotification ne true'];
    if (filters.userEmail) {
      const e = filters.userEmail.replace(/'/g, "''");
      clauses.push(`(RequesterEmail eq '${e}' or RecipientEmail eq '${e}')`);
    }
    if (filters.since) clauses.push(`Created ge datetime'${filters.since}'`);
    const top = filters.limit ?? 200;
    const qs = `$filter=${clauses.join(' and ')}&$orderby=Created desc&$top=${top}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ORDER_WORKFLOW_TRIGGERS)}?${qs}`)
    );
    return result.value.map(mapOrderWorkflowTriggerNotification);
  }

  async hideWorkflowTriggerNotification(id: string): Promise<void> {
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.WORKFLOW_TRIGGERS, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify({ HideNotification: true, Hide_x0020_notification: true }),
      })
    );
  }

  async hideOrderWorkflowTriggerNotification(id: string): Promise<void> {
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.ORDER_WORKFLOW_TRIGGERS, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify({ HideNotification: true, Hide_x0020_notification: true }),
      })
    );
  }

  async markWorkflowTriggerNotificationsRead(ids: string[]): Promise<void> {
    await Promise.all(ids.map(id =>
      withRetry(() =>
        this.fetch(this.listItem(SP_LISTS.WORKFLOW_TRIGGERS, id), {
          method: 'PATCH',
          headers: { 'If-Match': '*' } as Record<string, string>,
          body: JSON.stringify({ Read: true }),
        })
      )
    ));
  }

  async markOrderTriggerNotificationsRead(ids: string[]): Promise<void> {
    await Promise.all(ids.map(id =>
      withRetry(() =>
        this.fetch(this.listItem(SP_LISTS.ORDER_WORKFLOW_TRIGGERS, id), {
          method: 'PATCH',
          headers: { 'If-Match': '*' } as Record<string, string>,
          body: JSON.stringify({ Read: true }),
        })
      )
    ));
  }

  // ── Reference Data ──────────────────────────────────────────────────────────

  async getProjectsForUser(userId: string, periodMonth?: PeriodMonth): Promise<Project[]> {
    // Load all active projects, filter client-side by AssignedTeamEmails.
    // Only projects where the user is explicitly listed in AssignedTeamEmails are shown.
    // Standard non-project entries (Annual Leave, Sick Leave, G&A) are synthesized and always available.
    const active = await this.getAllActiveProjects();

    // Also fetch expired/closed projects that overlapped the given period month so users
    // can still log time against a project for dates within its active range even after expiry.
    let expired: Project[] = [];
    if (periodMonth) {
      const [year, month] = periodMonth.split('-').map(Number);
      const periodStart = `${periodMonth}-01`;
      const periodEnd = `${periodMonth}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
      const qs =
        `$filter=Status eq 'Closed' and StartDate le datetime'${periodEnd}T23:59:59' and EndDate ge datetime'${periodStart}T00:00:00'` +
        `&$select=Id,Title,ProjectCode,ProjectName,Type,CostCentre,` +
        `ProjectOwnerName,ProjectOwnerEmail,ProjectOwnerId,AssignedPMName,AssignedPMEmail,` +
        `AssignedPMId,StartDate,EndDate,EstimatedHours,` +
        `AssignedTeamEmails,AssignedTeamNames,AssignedTeamId,IsResearchDevelopment,Status,Description` +
        `&$top=${SP_PAGE_SIZE}`;
      const result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PROJECTS)}?${qs}`)
      );
      expired = result.value.map(mapProject);
    }

    // Merge active + expired, deduplicate by projectCode (active takes precedence)
    const seen = new Set<string>(active.map(p => p.projectCode));
    const merged = [...active, ...expired.filter(p => !seen.has(p.projectCode))];

    const userProjects = merged.filter(p => p.assignedTeamEmails.includes(userId));
    return [...userProjects, ...STANDARD_NON_PROJECT_ENTRIES];
  }

  async getAllActiveProjects(): Promise<Project[]> {
    const qs =
      "$filter=Status eq 'Active'&$select=Id,Title,ProjectCode,ProjectName,Type,CostCentre," +
      'ProjectOwnerName,ProjectOwnerEmail,' +
      'ProjectOwnerId,AssignedPMName,AssignedPMEmail,' +
      'AssignedPMId,StartDate,EndDate,EstimatedHours,' +
      'AssignedTeamEmails,AssignedTeamNames,' +
      'AssignedTeamId,IsResearchDevelopment,Status,Description&' +
      `$top=${SP_PAGE_SIZE}`;

    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PROJECTS)}?${qs}`)
    );
    return result.value.map(mapProject);
  }

  async getAllProjects(): Promise<Project[]> {
    const qs =
      '$select=Id,Title,ProjectCode,ProjectName,Type,CostCentre,' +
      'ProjectOwnerName,ProjectOwnerEmail,' +
      'ProjectOwnerId,AssignedPMName,AssignedPMEmail,' +
      'AssignedPMId,StartDate,EndDate,EstimatedHours,' +
      'AssignedTeamEmails,AssignedTeamNames,' +
      `AssignedTeamId,IsResearchDevelopment,Status,Description&$top=${SP_PAGE_SIZE}`;

    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PROJECTS)}?${qs}`)
    );
    return [...result.value.map(mapProject), ...STANDARD_NON_PROJECT_ENTRIES];
  }

  async createProject(data: ProjectData): Promise<Project> {
    const spIds = await this.resolveProjectPeople(data);
    const body = buildProjectBody(data, spIds);
    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.PROJECTS), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapProject(result);
  }

  async updateProject(id: string, data: Partial<ProjectData>): Promise<void> {
    const spIds = await this.resolveProjectPeople(data);
    const body = buildProjectBody(data, spIds);
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.PROJECTS, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  private async resolveProjectPeople(data: Partial<ProjectData>): Promise<{
    projectOwnerSpId?: number;
    assignedPMSpId?: number;
    assignedTeamSpIds?: number[];
  }> {
    const result: { projectOwnerSpId?: number; assignedPMSpId?: number; assignedTeamSpIds?: number[] } = {};
    await Promise.all([
      data.projectOwnerEmail
        ? this.ensureUser(data.projectOwnerEmail).then(id => { result.projectOwnerSpId = id; })
        : Promise.resolve(),
      data.assignedPMEmail
        ? this.ensureUser(data.assignedPMEmail).then(id => { result.assignedPMSpId = id; })
        : Promise.resolve(),
      data.assignedTeamEmails !== undefined
        ? Promise.all(
            data.assignedTeamEmails.split(',').filter(e => e.trim()).map(e => this.ensureUser(e.trim()))
          ).then(ids => { result.assignedTeamSpIds = ids; })
        : Promise.resolve(),
    ]);
    return result;
  }

  async deleteProject(id: string): Promise<void> {
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.PROJECTS, id), {
        method: 'DELETE',
        headers: { 'If-Match': '*' } as Record<string, string>,
      })
    );
  }

  async getCurrentUser(): Promise<Person> {
    const spUser = await withRetry(() =>
      this.fetch<{ LoginName: string; Id: number; Email: string }>('/web/currentuser?$select=Id,LoginName,Email')
    );
    const email = spUser.Email || spUser.LoginName.split('|').pop() || spUser.LoginName;

    const qs = `$filter=M365Email eq '${email}'&$top=1`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    if (result.value.length === 0) {
      throw new Error(`Current user not found in PEOPLE list (M365Email: ${email})`);
    }
    return mapPerson(result.value[0]);
  }

  async getPerson(personId: string): Promise<Person> {
    const raw = await withRetry(() =>
      this.fetch<SPRaw>(this.listItem(SP_LISTS.PEOPLE, personId))
    );
    return mapPerson(raw);
  }

  async getPersonByEmail(email: string): Promise<Person | null> {
    const qs = `$filter=M365Email eq '${encodeURIComponent(email)}'&$top=1`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    return result.value.length > 0 ? mapPerson(result.value[0]) : null;
  }

  async getPeople(filters: PeopleFilters = {}): Promise<Person[]> {
    const clauses: string[] = [];
    if (filters.active !== undefined) clauses.push(`Active eq ${filters.active ? 1 : 0}`);
    if (filters.role) clauses.push(`Role eq '${filters.role}'`);

    const filterStr = clauses.length > 0 ? `$filter=${clauses.join(' and ')}&` : '';
    const qs = `${filterStr}$top=${SP_PAGE_SIZE}`;

    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    return result.value.map(mapPerson);
  }

  async getDirectReports(managerEmail: string): Promise<Person[]> {
    const qs = `$filter=LineManagerEmail eq '${managerEmail}' and Active eq 1&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    return result.value.map(mapPerson);
  }

  async getBackupReports(backupApproverEmail: string): Promise<Person[]> {
    const qs = `$filter=BackupApproverEmail eq '${backupApproverEmail}' and Active eq 1&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    return result.value.map(mapPerson);
  }

  async getSuperApproverReports(superApproverEmail: string): Promise<Person[]> {
    const qs = `$filter=SuperApproverEmail eq '${superApproverEmail}' and Active eq 1&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PEOPLE)}?${qs}`)
    );
    return result.value.map(mapPerson);
  }

  async createPerson(data: PersonData): Promise<Person> {
    const body = buildPersonBody(data);
    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.PEOPLE), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapPerson(result);
  }

  async updatePerson(id: string, data: Partial<PersonData>): Promise<void> {
    const body = buildPersonBody(data);
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.PEOPLE, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  private async ensureUser(email: string): Promise<number> {
    const result = await withRetry(() =>
      this.fetch<{ Id: number }>('/web/ensureuser', {
        method: 'POST',
        body: JSON.stringify({ logonName: `i:0#.f|membership|${email}` }),
      })
    );
    return result.Id;
  }

  private async cachedEnsureUser(email: string): Promise<number> {
    const cached = this._spUserIdCache.get(email);
    if (cached !== undefined) return cached;
    const id = await this.ensureUser(email);
    this._spUserIdCache.set(email, id);
    return id;
  }

  private async resolvePersonAndApproverIds<T extends Partial<TimeEntry | NewTimeEntry>>(entry: T): Promise<T> {
    const [managerId, superApproverId] = await Promise.all([
      entry.managerEmail && entry.managerId == null
        ? this.cachedEnsureUser(entry.managerEmail).catch(() => null)
        : Promise.resolve(entry.managerId ?? null),
      entry.superApproverEmail && entry.superApproverId == null
        ? this.cachedEnsureUser(entry.superApproverEmail).catch(() => null)
        : Promise.resolve(entry.superApproverId ?? null),
    ]);
    return { ...entry, managerId, superApproverId };
  }

  async getAssets(): Promise<Asset[]> {
    const qs =
      `$select=Id,Title,Description,ResponsiblePersonEmail,ResponsiblePersonName,Active&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ASSETS)}?${qs}`)
    );
    return result.value.map(mapAsset);
  }

  async createAsset(data: AssetData): Promise<Asset> {
    const emails = (data.responsiblePeopleEmails?.filter(Boolean) ?? [])
      .concat(data.responsiblePersonEmail && !data.responsiblePeopleEmails?.length ? [data.responsiblePersonEmail] : []);
    let responsiblePersonIds: number[] | undefined;
    if (emails.length > 0) {
      try {
        responsiblePersonIds = await Promise.all(emails.map(e => this.ensureUser(e.trim())));
      } catch {
        // SP user resolution failed — text fields still written, lookup field skipped
      }
    }
    const body = buildAssetBody({ ...data, responsiblePersonIds });
    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.ASSETS), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapAsset(result);
  }

  async updateAsset(id: string, data: Partial<AssetData>): Promise<void> {
    const emails = (data.responsiblePeopleEmails?.filter(Boolean) ?? [])
      .concat(data.responsiblePersonEmail && !data.responsiblePeopleEmails?.length ? [data.responsiblePersonEmail] : []);
    let responsiblePersonIds: number[] | undefined;
    if (emails.length > 0) {
      try {
        responsiblePersonIds = await Promise.all(emails.map(e => this.ensureUser(e.trim())));
      } catch {
        // SP user resolution failed — text fields still written, lookup field skipped
      }
    }
    const body = buildAssetBody({ ...data, responsiblePersonIds });
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.ASSETS, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  async getAssetPermittedMembers(): Promise<Set<string>> {
    // Flat list: any member listed here may fill in any asset's timesheet.
    // Tolerate either a Person people-picker (expanded to EMail) or a
    // PersonEmail text column.
    const qs =
      `$select=Id,Person/Id,Person/EMail,Person/Title,PersonEmail` +
      `&$expand=Person&$top=${SP_PAGE_SIZE}`;

    let result: { value: SPRaw[] };
    try {
      result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `${this.listItems(SP_LISTS.ASSET_TIMESHEET_PERMITTED_GROUP)}?${qs}`,
        )
      );
    } catch {
      // List may not exist yet in every environment — treat as "no permitted
      // members" rather than blocking the whole Asset Timesheets page.
      return new Set();
    }

    const emails = new Set<string>();
    for (const raw of result.value) {
      const personObj = raw.Person && typeof raw.Person === 'object'
        ? (raw.Person as { EMail: unknown })
        : null;
      const email = (str(personObj?.EMail) || str(raw.PersonEmail)).trim().toLowerCase();
      if (email) emails.add(email);
    }
    return emails;
  }

  async getUnlockPermittedMembers(): Promise<Set<string>> {
    const qs =
      `$select=Id,Person/Id,Person/EMail,Person/Title,PersonEmail` +
      `&$expand=Person&$top=${SP_PAGE_SIZE}`;

    let result: { value: SPRaw[] };
    try {
      result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `${this.listItems(SP_LISTS.UNLOCK_TIMESHEET_PERMITTED_GROUP)}?${qs}`,
        )
      );
    } catch {
      return new Set();
    }

    const emails = new Set<string>();
    for (const raw of result.value) {
      const personObj = raw.Person && typeof raw.Person === 'object'
        ? (raw.Person as { EMail: unknown })
        : null;
      const email = (str(personObj?.EMail) || str(raw.PersonEmail)).trim().toLowerCase();
      if (email) emails.add(email);
    }
    return emails;
  }

  async getOrderMgmtGroupMembers(): Promise<Set<string>> {
    const qs = `$select=PersonEmail&$top=${SP_PAGE_SIZE}`;
    let result: { value: SPRaw[] };
    try {
      result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `${this.listItems(SP_LISTS.ORDER_MGMT_GROUP)}?${qs}`,
        )
      );
    } catch {
      return new Set();
    }
    const emails = new Set<string>();
    for (const raw of result.value) {
      const email = str(raw.PersonEmail).trim().toLowerCase();
      if (email) emails.add(email);
    }
    return emails;
  }

  async getOrderingPermittedMembers(): Promise<Set<string>> {
    const qs =
      `$select=Id,Title,TargetUsers/EMail,TargetUsers/Title` +
      `&$expand=TargetUsers` +
      `&$top=100`;

    let result: { value: SPRaw[] };
    try {
      result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `${this.listItems(SP_LISTS.SECURITY_CONFIGURATIONS)}?${qs}`,
        )
      );
    } catch (err) {
      console.error('[getOrderingPermittedMembers] fetch failed:', err);
      return new Set();
    }

    const row = result.value.find(
      r => str(r.Title).trim().toLowerCase() === 'ordering'
    );

    const emails = new Set<string>();
    if (!row) return emails;

    const users = row.TargetUsers;
    const userArr: unknown[] = Array.isArray(users)
      ? users
      : (users && typeof users === 'object' && Array.isArray((users as { value?: unknown[] }).value))
        ? (users as { value: unknown[] }).value
        : [];

    for (const u of userArr) {
      const email = str((u as { EMail?: unknown }).EMail).trim().toLowerCase();
      if (email) emails.add(email);
    }
    return emails;
  }

  async getBillOutRatesPermittedMembers(): Promise<Set<string>> {
    // Fetch all Security Configurations items and filter in JS to avoid OData
    // filter issues with string values containing special characters.
    const qs =
      `$select=Id,Title,TargetUsers/EMail,TargetUsers/Title` +
      `&$expand=TargetUsers` +
      `&$top=100`;

    let result: { value: SPRaw[] };
    try {
      result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `${this.listItems(SP_LISTS.SECURITY_CONFIGURATIONS)}?${qs}`,
        )
      );
    } catch (err) {
      console.error('[getBillOutRatesPermittedMembers] fetch failed:', err);
      return new Set();
    }

    const row = result.value.find(
      r => str(r.Title).trim().toLowerCase() === 'bill-out rates'
    );
    console.log('[getBillOutRatesPermittedMembers] row:', row);

    const emails = new Set<string>();
    if (!row) return emails;

    // SharePoint may return the expanded UserMulti collection as a plain array
    // (odata=nometadata) or wrapped in { value: [...] } depending on the site version.
    const users = row.TargetUsers;
    const userArr: unknown[] = Array.isArray(users)
      ? users
      : (users && typeof users === 'object' && Array.isArray((users as { value?: unknown[] }).value))
        ? (users as { value: unknown[] }).value
        : [];

    for (const u of userArr) {
      const email = str((u as { EMail?: unknown }).EMail).trim().toLowerCase();
      if (email) emails.add(email);
    }
    console.log('[getBillOutRatesPermittedMembers] emails:', [...emails]);
    return emails;
  }

  async getBankHolidays(year: number): Promise<BankHoliday[]> {
    const qs = `$filter=Year eq ${year} and IsActive eq 1&$top=50`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.BANK_HOLIDAYS)}?${qs}`)
    );
    return result.value.map(mapBankHoliday);
  }

  // ── Bill-Out Rates ──────────────────────────────────────────────────────────

  async getBillOutRates(filters: BillOutRateFilters = {}): Promise<BillOutRate[]> {
    const clauses: string[] = [];
    if (filters.costGroup) clauses.push(`CostGroup eq '${filters.costGroup}'`);

    const filterStr = clauses.length > 0 ? `$filter=${clauses.join(' and ')}&` : '';
    const qs =
      `${filterStr}$select=Id,Title,CostGroup,BillRate,CostRate,EffectiveFrom,EffectiveTo,Notes,Created,Modified` +
      `&$orderby=CostGroup asc,EffectiveFrom desc&$top=${SP_PAGE_SIZE}`;

    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.BILL_OUT_RATES)}?${qs}`)
    );
    return result.value.map(mapBillOutRate);
  }

  async createBillOutRate(data: BillOutRateData): Promise<BillOutRate> {
    const body = buildBillOutRateBody(data);
    const result = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.BILL_OUT_RATES), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
    return mapBillOutRate(result);
  }

  async updateBillOutRate(id: string, data: Partial<BillOutRateData>): Promise<void> {
    const body = buildBillOutRateBody(data);
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.BILL_OUT_RATES, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  async deleteBillOutRate(id: string): Promise<void> {
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.BILL_OUT_RATES, id), {
        method: 'DELETE',
        headers: { 'If-Match': '*' } as Record<string, string>,
      })
    );
  }

  async lookupBillOutRate(costGroup: string, date: ISODateString): Promise<BillOutRate | null> {
    const rates = await this.getBillOutRates({ costGroup });
    const candidates = rates.filter(r => {
      if (r.effectiveFrom > date) return false;
      if (r.effectiveTo !== null && r.effectiveTo < date) return false;
      return true;
    });
    if (candidates.length === 0) return null;
    // Prefer the record with the latest effectiveFrom (already sorted desc by the query)
    return candidates[0];
  }

  // ── Lookup / Master Lists ────────────────────────────────────────────────────

  async getRoleOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ROLES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title ?? raw.Title)).filter(Boolean);
  }

  async getCostTypeOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.COST_TYPES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getCostTypes(): Promise<CostTypeItem[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.COST_TYPES)}?$select=Id,Title,CostRate&$top=100`)
    );
    return result.value
      .filter(raw => str(raw.Title))
      .map(raw => ({
        id: str(raw.Id),
        name: str(raw.Title),
        costRate: raw.CostRate != null ? num(raw.CostRate) : 0,
      }));
  }

  async getProjectTypeOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PROJECT_TYPES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getCostCentreOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.COST_CENTRES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getUnitOfMeasureOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.UNIT_OF_MEASURES)}?$select=Id,Title&$orderby=Title asc&$top=500`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getProjectStatusOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.PROJECT_STATUSES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getTimesheetStatusOptions(): Promise<string[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.TIMESHEET_STATUSES)}?$select=Id,Title&$top=100`)
    );
    return result.value.map(raw => str(raw.Title)).filter(Boolean);
  }

  async getLineManagerOptions(): Promise<PersonOption[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.LINE_MANAGERS)}?$select=Id,Title,Email&$top=500`)
    );
    return result.value.map(raw => ({ name: str(raw.Title), email: str(raw.Email) })).filter(o => o.name);
  }

  async getBackupApproverOptions(): Promise<PersonOption[]> {
    // Read from the PEOPLE list so the email here is M365Email — the same value
    // getBackupReports queries against. Using the separate BACKUP_APPROVERS list
    // caused a mismatch where the stored BackupApproverEmail didn't match
    // the person's M365Email, so backup approvers saw no entries in My Approvals.
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(
        `${this.listItems(SP_LISTS.PEOPLE)}?$select=Id,FullName,M365Email&$filter=Active eq 1&$orderby=FullName&$top=500`
      )
    );
    return result.value
      .map(raw => ({ name: str(raw.FullName ?? raw.Title), email: str(raw.M365Email) }))
      .filter(o => o.name && o.email);
  }

  async getSuperApproverOptions(): Promise<PersonOption[]> {
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.SUPER_APPROVERS)}?$select=Id,Title,Email&$top=500`)
    );
    return result.value.map(raw => ({ name: str(raw.Title), email: str(raw.Email) })).filter(o => o.name);
  }

  // ── Ordering ─────────────────────────────────────────────────────────────────

  async getOrders(filters?: OrderFilters): Promise<Order[]> {
    const clauses: string[] = [];
    if (filters?.requesterEmail) clauses.push(`RequesterEmail eq '${filters.requesterEmail}'`);
    if (filters?.status) clauses.push(`Status eq '${filters.status}'`);

    const filterStr = clauses.length > 0 ? `$filter=${clauses.join(' and ')}` : '';
    const selectStr =
      '$select=Id,Title,OrderRef,RequesterEmail,RequesterName,CostAllocation,ProjectCode,ProjectName,' +
      'Supplier,Priority,Currency,TotalAmount,SterlingEquivalent,OrderDate,RequiredByDate,' +
      'ExpectedDeliveryDate,ActualDeliveryDate,Status,Notes,PONumber,CostCentreName,' +
      'ApprovedOnBehalfOf,HasChemicalLines,IsLocked,FYIViewerEmails,LineManagerEmail,LineManagerName,' +
      'CFOApproverEmail,EscalatedApproverEmail,EscalatedApproverName,Created,Modified';

    const qs = [filterStr, selectStr, '$orderby=Id desc', `$top=${SP_PAGE_SIZE}`].filter(Boolean).join('&');

    const allItems: SPRaw[] = [];
    let nextPath: string | null = `${this.listItems(SP_LISTS.ORDERS)}?${qs}`;
    while (nextPath) {
      const result = await withRetry(() => this.fetch<{ value: SPRaw[]; 'odata.nextLink'?: string }>(nextPath!));
      allItems.push(...result.value);
      const nextLink = result['odata.nextLink'];
      if (nextLink) {
        const apiIdx = nextLink.indexOf('/_api');
        nextPath = apiIdx >= 0 ? nextLink.slice(apiIdx + 5) : null;
      } else {
        nextPath = null;
      }
    }

    // Post-filter for FYI viewer (comma-separated email field — SP doesn't support contains filter)
    if (filters?.fyiViewerEmail) {
      const email = filters.fyiViewerEmail.trim().toLowerCase();
      return allItems
        .map(mapOrder)
        .filter(o => o.fyiViewerEmails.some(e => e.trim().toLowerCase() === email));
    }
    return allItems.map(mapOrder);
  }

  async getOrderById(id: string): Promise<Order> {
    const selectStr =
      '$select=Id,Title,OrderRef,RequesterEmail,RequesterName,CostAllocation,ProjectCode,ProjectName,' +
      'Supplier,Priority,Currency,TotalAmount,SterlingEquivalent,OrderDate,RequiredByDate,' +
      'ExpectedDeliveryDate,ActualDeliveryDate,Status,Notes,PONumber,CostCentreName,' +
      'ApprovedOnBehalfOf,HasChemicalLines,IsLocked,FYIViewerEmails,LineManagerEmail,LineManagerName,' +
      'CFOApproverEmail,EscalatedApproverEmail,EscalatedApproverName,Created,Modified';
    const raw = await withRetry(() =>
      this.fetch<SPRaw>(`${this.listItem(SP_LISTS.ORDERS, id)}?${selectStr}`)
    );
    return mapOrder(raw);
  }

  async createOrder(data: NewOrderData): Promise<Order> {
    const body = buildOrderBody(data as Partial<OrderData>);
    const raw = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.ORDERS), { method: 'POST', body: JSON.stringify(body) })
    );
    const orderRef = `ORD-${String(raw.Id as number).padStart(4, '0')}`;
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.ORDERS, String(raw.Id)), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify({ OrderRef: orderRef }),
      })
    );
    return mapOrder({ ...raw, OrderRef: orderRef });
  }

  async updateOrder(id: string, data: Partial<OrderData>): Promise<void> {
    const body = buildOrderBody(data);
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.ORDERS, id), {
        method: 'PATCH',
        headers: { 'If-Match': '*' } as Record<string, string>,
        body: JSON.stringify(body),
      })
    );
  }

  async getOrderLineItems(orderId: number): Promise<OrderLineItem[]> {
    const qs =
      `$filter=OrderId eq ${orderId}` +
      `&$select=Id,OrderId,ItemDescription,SKU,ItemLink,Quantity,UnitOfMeasure,UnitPrice,Subtotal,IsChemical,COSHHRef,LineOrder` +
      `&$orderby=LineOrder asc,Id asc&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ORDER_LINE_ITEMS)}?${qs}`)
    );
    return result.value.map(mapOrderLineItem);
  }

  async saveOrderLineItems(orderId: number, items: NewLineItemData[]): Promise<OrderLineItem[]> {
    const tasks = items.map((item, idx) => async () => {
      const body = buildLineItemBody({ ...item, orderId, lineOrder: idx + 1 });
      const raw = await withRetry(() =>
        this.fetch<SPRaw>(this.listItems(SP_LISTS.ORDER_LINE_ITEMS), { method: 'POST', body: JSON.stringify(body) })
      );
      return mapOrderLineItem(raw);
    });
    return Promise.all(tasks.map(t => t()));
  }

  async deleteOrderLineItem(id: string): Promise<void> {
    await withRetry(() =>
      this.fetch(this.listItem(SP_LISTS.ORDER_LINE_ITEMS, id), {
        method: 'DELETE',
        headers: { 'If-Match': '*' } as Record<string, string>,
      })
    );
  }

  async getOrderApprovalLog(orderId: number): Promise<OrderApprovalLog[]> {
    const qs =
      `$filter=OrderId eq ${orderId}` +
      `&$select=Id,OrderId,ApprovalStage,ApproverName,ApproverEmail,Decision,Note,OnBehalfOf,ApprovalDate,AttachmentRef` +
      `&$orderby=ApprovalDate asc&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ORDER_APPROVAL_LOG)}?${qs}`)
    );
    return result.value.map(mapOrderApprovalLog);
  }

  async addApprovalDecision(data: NewApprovalData): Promise<OrderApprovalLog> {
    const body = buildApprovalBody(data);
    const raw = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.ORDER_APPROVAL_LOG), { method: 'POST', body: JSON.stringify(body) })
    );
    return mapOrderApprovalLog({ ...body, Id: raw.Id });
  }

  async getOrderComments(orderId: number): Promise<OrderComment[]> {
    const qs =
      `$filter=OrderId eq ${orderId}` +
      `&$select=Id,OrderId,AuthorName,AuthorEmail,CommentText,CommentDate` +
      `&$orderby=CommentDate asc&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.ORDER_COMMENTS)}?${qs}`)
    );
    return result.value.map(mapOrderComment);
  }

  async addOrderComment(data: NewCommentData): Promise<OrderComment> {
    const now = new Date().toISOString();
    const body = buildCommentBody(data, now);
    const raw = await withRetry(() =>
      this.fetch<SPRaw>(this.listItems(SP_LISTS.ORDER_COMMENTS), { method: 'POST', body: JSON.stringify(body) })
    );
    return mapOrderComment({ ...body, Id: raw.Id, CommentDate: now });
  }

  async getCOSHHRegister(): Promise<COSHHEntry[]> {
    const qs =
      `$select=Id,Title,ChemicalName,HazardType,Description&$orderby=Title asc&$top=${SP_PAGE_SIZE}`;
    const result = await withRetry(() =>
      this.fetch<{ value: SPRaw[] }>(`${this.listItems(SP_LISTS.COSHH_REGISTER)}?${qs}`)
    );
    return result.value.map(mapCOSHHEntry);
  }

  // ── Order Attachments (document library) ───────────────────────────────────

  /** Returns the server-relative site path, e.g. "/sites/mysite". */
  private get siteRelPath(): string {
    return new URL(this.baseUrl).pathname.replace(/\/$/, '');
  }

  /**
   * Creates the subfolder "Order Attachments/{orderRef}" if it doesn't exist.
   * Any error (including "already exists") is silently swallowed so the
   * subsequent upload attempt can fail loudly if something is actually wrong.
   */
  private async ensureOrderFolder(orderRef: string): Promise<void> {
    const libRelUrl = `${this.siteRelPath}/${SP_LISTS.ORDER_ATTACHMENTS}`;
    try {
      await this.fetch<unknown>(
        `/web/GetFolderByServerRelativeUrl('${encodeURIComponent(libRelUrl)}')/Folders/add('${encodeURIComponent(orderRef)}')`,
        { method: 'POST' }
      );
    } catch {
      // Folder already exists or library path slightly off — upload will surface any real error.
    }
  }

  async uploadOrderAttachment(
    _orderId: number,
    orderRef: string,
    file: File,
    category: AttachmentCategory
  ): Promise<OrderAttachment> {
    await this.ensureOrderFolder(orderRef);

    // Build a collision-safe filename: "category_YYYYMMDDHHMMSS_originalname"
    const ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileName = `${category}_${ts}_${safeName}`;

    const folderRelUrl = `${this.siteRelPath}/${SP_LISTS.ORDER_ATTACHMENTS}/${orderRef}`;
    const arrayBuffer = await file.arrayBuffer();

    const raw = await withRetry(() =>
      this.fetch<SPRaw>(
        `/web/GetFolderByServerRelativeUrl('${encodeURIComponent(folderRelUrl)}')/Files/Add(url='${encodeURIComponent(fileName)}',overwrite=true)`,
        {
          method: 'POST',
          body: arrayBuffer,
          headers: { 'Content-Type': 'application/octet-stream' } as Record<string, string>,
        }
      )
    );

    return {
      name: fileName,
      serverRelUrl: str(raw.ServerRelativeUrl),
      uploadedAt: new Date().toISOString(),
      category,
    };
  }

  async getOrderAttachments(orderRef: string): Promise<OrderAttachment[]> {
    const folderRelUrl = `${this.siteRelPath}/${SP_LISTS.ORDER_ATTACHMENTS}/${orderRef}`;
    try {
      const result = await withRetry(() =>
        this.fetch<{ value: SPRaw[] }>(
          `/web/GetFolderByServerRelativeUrl('${encodeURIComponent(folderRelUrl)}')/Files` +
          `?$select=Name,ServerRelativeUrl,TimeCreated&$orderby=TimeCreated asc`
        )
      );
      return result.value.map(f => {
        const name = str(f.Name);
        const cat: AttachmentCategory = name.startsWith('ceo-approval') ? 'ceo-approval'
          : name.startsWith('quote') ? 'quote'
          : 'other';
        return {
          name,
          serverRelUrl: str(f.ServerRelativeUrl),
          uploadedAt: str(f.TimeCreated),
          category: cat,
        };
      });
    } catch {
      // Folder not yet created (no uploads) → empty array
      return [];
    }
  }

  async downloadOrderAttachment(serverRelUrl: string): Promise<Blob> {
    const token = await this.getToken();
    const url = `${this.baseUrl}/_api/web/GetFileByServerRelativeUrl('${encodeURIComponent(serverRelUrl)}')/$value`;
    const response = await globalThis.fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new SharePointError(
        `File download failed (${response.status})`,
        response.status
      );
    }
    return response.blob();
  }
}

// ─── Mapping Helpers ──────────────────────────────────────────────────────────

function str(v: unknown, fallback = ''): string {
  return v != null ? String(v) : fallback;
}

function num(v: unknown): number {
  return v != null ? Number(v) : 0;
}

function bool(v: unknown): boolean {
  return Boolean(v);
}

function isoDate(v: unknown): ISODateString | null {
  if (!v) return null;
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return null;
  return formatISODate(d);
}

function mapTimeEntry(raw: SPRaw): TimeEntry {
  return {
    id: str(raw.Id) as TimeEntry['id'],
    personEmail: str(raw.PersonEmail),
    personName: str(raw.PersonName),
    projectCode: str(raw.ProjectCode),
    dateWorked: formatISODate(new Date(str(raw.DateWorked))),
    periodMonth: toPeriodMonth(str(raw.PeriodMonth, '1970-01')),
    hours: num(raw.Hours),
    status: str(raw.Status, TimesheetStatus.Draft) as TimesheetStatus,
    managerName: str(raw.ManagerName),
    managerEmail: str(raw.ManagerEmail),
    managerId: raw.ManagerId != null ? num(raw.ManagerId) : null,
    managerApprovedDate: isoDate(raw.ManagerApprovedDate),
    superApproverName: str(raw.SuperApproverName),
    superApproverEmail: str(raw.SuperApproverEmail),
    superApproverId: raw.SuperApproverId != null ? num(raw.SuperApproverId) : null,
    superApprovedDate: isoDate(raw.SuperApprovedDate),
    submittedDate: isoDate(raw.SubmittedDate),
    lockedDate: isoDate(raw.LockedDate),
    unlockedDate: isoDate(raw.UnlockedDate),
    unlockReason: str(raw.UnlockReason),
    unlockApprovedBy: str(raw.UnlockApprovedBy),
    rejectionNote: str(raw.RejectionNote),
    billRate: raw.BillRate != null ? num(raw.BillRate) : null,
    costRate: raw.CostRate != null ? num(raw.CostRate) : null,
    taskDescription: str(raw.TaskDescription),
    billableToClient: bool(raw.BillableToClient),
    assetCode: str(raw.AssetCode),
    created: raw.Created ? formatISODate(new Date(str(raw.Created))) : toISODateString('1970-01-01'),
    modified: raw.Modified ? formatISODate(new Date(str(raw.Modified))) : toISODateString('1970-01-01'),
  };
}

function mapPerson(raw: SPRaw): Person {
  return {
    id: str(raw.Id) as Person['id'],
    fullName: str(raw.FullName ?? raw.Title),
    m365Email: str(raw.M365Email),
    department: str(raw.Department),
    role: str(raw.Role, PersonRole.Colleague) as PersonRole,
    lineManager: str(raw.LineManager),
    lineManagerEmail: str(raw.LineManagerEmail),
    backupApprover: str(raw.BackupApprover),
    backupApproverEmail: str(raw.BackupApproverEmail),
    superApprover: str(raw.SuperApprover),
    superApproverEmail: str(raw.SuperApproverEmail),
    costType: str(raw.CostType, CostType.Fixed) as CostType,
    hourlyRate: raw.HourlyRate != null ? num(raw.HourlyRate) : null,
    exemptFromReminders: bool(raw.ExemptFromReminders),
    exemptionStart: isoDate(raw.ExemptionStart),
    exemptionEnd: isoDate(raw.ExemptionEnd),
    isTimesheetMgmtGroup: bool(raw.IsTimesheetMgmtGroup),
    active: bool(raw.Active),
    aadObjectId: str(raw.AADObjectId),
    purchaseApprovalLimit: raw.PurchaseApprovalLimit != null ? num(raw.PurchaseApprovalLimit) : null,
    isChemicalApprover: bool(raw.IsChemicalApprover),
    isOrderMgmtGroup: bool(raw.IsOrderMgmtGroup),
    isCFO: bool(raw.IsCFO),
    canApproveOnBehalfOfCEO: bool(raw.CanApproveOnBehalfOfCEO),
  };
}

function buildPersonBody(data: Partial<PersonData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.fullName !== undefined) body.FullName = data.fullName;
  if (data.m365Email !== undefined) body.M365Email = data.m365Email;
  if (data.department !== undefined) body.Department = data.department;
  if (data.role !== undefined) body.Role = data.role;
  if (data.lineManager !== undefined) body.LineManager = data.lineManager;
  if (data.lineManagerEmail !== undefined) body.LineManagerEmail = data.lineManagerEmail;
  if (data.backupApprover !== undefined) body.BackupApprover = data.backupApprover;
  if (data.backupApproverEmail !== undefined) body.BackupApproverEmail = data.backupApproverEmail;
  if (data.superApprover !== undefined) body.SuperApprover = data.superApprover;
  if (data.superApproverEmail !== undefined) body.SuperApproverEmail = data.superApproverEmail;
  if (data.costType !== undefined) body.CostType = data.costType;
  if (data.hourlyRate !== undefined) body.HourlyRate = data.hourlyRate;
  if (data.exemptFromReminders !== undefined) body.ExemptFromReminders = data.exemptFromReminders;
  if (data.exemptionStart !== undefined) body.ExemptionStart = data.exemptionStart;
  if (data.exemptionEnd !== undefined) body.ExemptionEnd = data.exemptionEnd;
  if (data.isTimesheetMgmtGroup !== undefined) body.IsTimesheetMgmtGroup = data.isTimesheetMgmtGroup;
  if (data.active !== undefined) body.Active = data.active;
  if (data.purchaseApprovalLimit !== undefined) body.PurchaseApprovalLimit = data.purchaseApprovalLimit;
  if (data.isChemicalApprover !== undefined) body.IsChemicalApprover = data.isChemicalApprover;
  if (data.isOrderMgmtGroup !== undefined) body.IsOrderMgmtGroup = data.isOrderMgmtGroup;
  if (data.isCFO !== undefined) body.IsCFO = data.isCFO;
  if (data.canApproveOnBehalfOfCEO !== undefined) body.CanApproveOnBehalfOfCEO = data.canApproveOnBehalfOfCEO;
  return body;
}

function buildProjectBody(
  data: Partial<ProjectData>,
  spIds?: { projectOwnerSpId?: number; assignedPMSpId?: number; assignedTeamSpIds?: number[] }
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.projectCode !== undefined) { body.Title = data.projectCode; body.ProjectCode = data.projectCode; }
  if (data.projectName !== undefined) body.ProjectName = data.projectName;
  if (data.type !== undefined) body.Type = data.type;
  if (data.costCentre !== undefined) body.CostCentre = data.costCentre;
  if (data.projectOwnerName !== undefined) body['ProjectOwnerName'] = data.projectOwnerName;
  if (data.projectOwnerEmail !== undefined) body['ProjectOwnerEmail'] = data.projectOwnerEmail;
  if (spIds?.projectOwnerSpId !== undefined) {
    body['ProjectOwnerId'] = spIds.projectOwnerSpId;
  }
  if (data.assignedPMName !== undefined) body['AssignedPMName'] = data.assignedPMName;
  if (data.assignedPMEmail !== undefined) body['AssignedPMEmail'] = data.assignedPMEmail;
  if (spIds?.assignedPMSpId !== undefined) {
    body['AssignedPMId'] = spIds.assignedPMSpId;
  }
  if (data.startDate !== undefined) body.StartDate = data.startDate;
  if (data.endDate !== undefined) body.EndDate = data.endDate;
  if (data.estimatedHours !== undefined) body.EstimatedHours = data.estimatedHours;
  if (data.assignedTeamEmails !== undefined) body['AssignedTeamEmails'] = data.assignedTeamEmails;
  if (data.assignedTeamNames !== undefined) body['AssignedTeamNames'] = data.assignedTeamNames;
  if (spIds?.assignedTeamSpIds !== undefined) body['AssignedTeamId'] = spIds.assignedTeamSpIds;
  if (data.isResearchDevelopment !== undefined) body.IsResearchDevelopment = data.isResearchDevelopment;
  if (data.status !== undefined) body.Status = data.status;
  if (data.description !== undefined) body.Description = data.description;
  return body;
}

function mapAsset(raw: SPRaw): Asset {
  const responsiblePeopleEmailsRaw = str(raw['Responsible Person Email'] ?? raw.ResponsiblePersonEmail);
  const responsiblePeopleEmails = responsiblePeopleEmailsRaw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const responsiblePeopleNamesRaw = str(raw['Responsible Person Name'] ?? raw.ResponsiblePersonName);
  const responsiblePeopleNames = responsiblePeopleNamesRaw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  return {
    id: str(raw.Id) as Asset['id'],
    title: str(raw.Title),
    description: str(raw.Description),
    responsiblePerson: '',
    responsiblePersonEmail: responsiblePeopleEmails[0] ?? '',
    responsiblePersonName: responsiblePeopleNames[0] ?? '',
    responsiblePeopleEmails,
    responsiblePeopleNames,
    active: bool(raw.Active),
  };
}

function buildAssetBody(data: Partial<AssetData> & { responsiblePersonIds?: number[] }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.title !== undefined) body.Title = data.title;
  if (data.description !== undefined) body.Description = data.description;
  if (data.responsiblePersonIds !== undefined) body.ResponsiblePersonId = data.responsiblePersonIds;
  if (data.responsiblePeopleEmails !== undefined) {
    body.ResponsiblePersonEmail = data.responsiblePeopleEmails.join(',');
    body.ResponsiblePersonName = (data.responsiblePeopleNames ?? []).join(',');
  } else {
    if (data.responsiblePersonEmail !== undefined) body.ResponsiblePersonEmail = data.responsiblePersonEmail;
    if (data.responsiblePersonName !== undefined) body.ResponsiblePersonName = data.responsiblePersonName;
  }
  if (data.active !== undefined) body.Active = data.active;
  return body;
}

function mapProject(raw: SPRaw): Project {
  const assignedTeamEmailsRaw = str(raw['Assigned Team Emails'] ?? raw.AssignedTeamEmails);
  const assignedTeamEmails = assignedTeamEmailsRaw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const assignedTeamNamesRaw = str(raw['Assigned Team Names'] ?? raw.AssignedTeamNames);
  const assignedTeamNames = assignedTeamNamesRaw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  return {
    id: str(raw.Id) as Project['id'],
    projectCode: str(raw.ProjectCode ?? raw.Title),
    projectName: str(raw.ProjectName ?? raw.Title),
    type: str(raw.Type, ProjectType.Standard) as ProjectType,
    costCentre: str(raw.CostCentre),
    projectOwnerName: str(raw['Project Owner Name'] ?? raw.ProjectOwnerName),
    projectOwnerEmail: str(raw['Project Owner Email'] ?? raw.ProjectOwnerEmail),
    assignedPMName: str(raw['Assigned PM Name'] ?? raw.AssignedPMName),
    assignedPMEmail: str(raw['Assigned PM Email'] ?? raw.AssignedPMEmail),
    startDate: toISODateString(str(raw.StartDate, '').split('T')[0] || '1970-01-01'),
    endDate: isoDate(raw.EndDate),
    estimatedHours: raw.EstimatedHours != null ? num(raw.EstimatedHours) : null,
    assignedTeamEmails,
    assignedTeamNames,
    isResearchDevelopment: bool(raw.IsResearchDevelopment),
    status: str(raw.Status, ProjectStatus.Active) as ProjectStatus,
    description: str(raw.Description),
  };
}

function parseSPHolidayDate(raw: string): string {
  if (!raw) return '1970-01-01';
  // If the string carries timezone info (Z or ±hh:mm), parse as Date and use
  // local date parts so BST dates don't shift back one day (e.g. April 3 00:00 BST
  // is returned as April 2 23:00Z by SharePoint, but local getDate() gives April 3).
  if (raw.endsWith('Z') || /[T ]\d{2}:\d{2}:\d{2}[+-]/.test(raw)) {
    const d = new Date(raw);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return raw.split('T')[0];
}

function mapChaseNotification(raw: SPRaw): ChaseNotification {
  return {
    id: str(raw.Id),
    personName: str(raw.PersonName),
    personEmail: str(raw.PersonEmail),
    periodMonth: toPeriodMonth(str(raw.PeriodMonth, '1970-01')),
    status: str(raw.Status, ChaseNotificationStatus.Pending) as ChaseNotificationStatus,
    created: str(raw.Created),
  };
}

function mapWorkflowTriggerNotification(raw: SPRaw): WorkflowTriggerNotification {
  return {
    id: str(raw.Id),
    title: str(raw.Title),
    actionType: str(raw.ActionType),
    status: str(raw.Status),
    periodMonth: str(raw.PeriodMonth),
    userName: str(raw.UserName),
    userEmail: str(raw.UserEmail),
    approverName: str(raw.ApproverName),
    approverEmail: str(raw.ApproverEmail),
    approverRole: str(raw.ApproverRole),
    source: str(raw.Source),
    isFinalState: bool(raw.IsFinalState),
    reason: raw.Reason ? str(raw.Reason) : undefined,
    rejectionComment: raw.RejectionComment ? str(raw.RejectionComment) : undefined,
    totalHours: raw.TotalHours != null ? num(raw.TotalHours) : undefined,
    timestamp: str(raw.Timestamp ?? raw.Created),
    hideNotification: bool(raw.HideNotification ?? raw.Hide_x0020_notification),
    read: bool(raw.Read),
    created: str(raw.Created),
  };
}

function mapOrderWorkflowTriggerNotification(raw: SPRaw): OrderWorkflowTriggerNotification {
  return {
    id: str(raw.Id),
    title: str(raw.Title),
    triggerType: str(raw.TriggerType),
    orderRef: str(raw.OrderRef),
    orderId: num(raw.OrderId),
    orderTitle: str(raw.OrderTitle),
    orderAmount: num(raw.OrderAmount),
    recipientName: str(raw.RecipientName),
    recipientEmail: str(raw.RecipientEmail),
    requesterName: str(raw.RequesterName),
    requesterEmail: str(raw.RequesterEmail),
    status: str(raw.Status),
    note: raw.Note ? str(raw.Note) : undefined,
    timestamp: str(raw.Timestamp ?? raw.Created),
    hideNotification: bool(raw.HideNotification ?? raw.Hide_x0020_notification),
    read: bool(raw.Read),
    created: str(raw.Created),
  };
}

function mapBankHoliday(raw: SPRaw): BankHoliday {
  return {
    id: str(raw.Id) as BankHoliday['id'],
    holidayDate: toISODateString(parseSPHolidayDate(str(raw.HolidayDate, '')) || '1970-01-01'),
    holidayName: str(raw.HolidayName ?? raw.Title),
    appliesTo: str(raw.AppliesTo, BankHolidayRegion.UK) as BankHolidayRegion,
    defaultHours: num(raw.DefaultHours) || 7.5,
    year: num(raw.Year),
    isActive: bool(raw.IsActive),
  };
}

/** Builds the JSON body for POST/PATCH (omits read-only fields). */
function buildTimeEntryBody(
  entry: Partial<TimeEntry | NewTimeEntry>
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  // DO NOT include Id, periodMonth (calculated), created, modified — SP manages these
  if (entry.personEmail !== undefined) body.PersonEmail = entry.personEmail;
  if (entry.personName !== undefined) body.PersonName = entry.personName;
  if (entry.projectCode !== undefined) body.ProjectCode = entry.projectCode;
  if (entry.dateWorked !== undefined) body.DateWorked = entry.dateWorked;
  if (entry.hours !== undefined) body.Hours = entry.hours;
  if (entry.status !== undefined) body.Status = entry.status;
  if (entry.managerName !== undefined) body.ManagerName = entry.managerName;
  if (entry.managerEmail !== undefined) body.ManagerEmail = entry.managerEmail;
  if (entry.managerId != null) body.ManagerId = entry.managerId;
  if (entry.managerApprovedDate !== undefined) body.ManagerApprovedDate = entry.managerApprovedDate;
  if (entry.superApproverName !== undefined) body.SuperApproverName = entry.superApproverName;
  if (entry.superApproverEmail !== undefined) body.SuperApproverEmail = entry.superApproverEmail;
  if (entry.superApproverId != null) body.SuperApproverId = entry.superApproverId;
  if (entry.superApprovedDate !== undefined) body.SuperApprovedDate = entry.superApprovedDate;
  if (entry.submittedDate !== undefined) body.SubmittedDate = entry.submittedDate;
  if (entry.lockedDate !== undefined) body.LockedDate = entry.lockedDate;
  if (entry.unlockedDate !== undefined) body.UnlockedDate = entry.unlockedDate;
  if (entry.unlockReason !== undefined) body.UnlockReason = entry.unlockReason;
  if (entry.unlockApprovedBy !== undefined) body.UnlockApprovedBy = entry.unlockApprovedBy;
  if (entry.rejectionNote !== undefined) body.RejectionNote = entry.rejectionNote;
  if (entry.billRate !== undefined) body.BillRate = entry.billRate;
  if (entry.costRate !== undefined) body.CostRate = entry.costRate;
  if (entry.taskDescription !== undefined) body.TaskDescription = entry.taskDescription;
  if (entry.billableToClient !== undefined) body.BillableToClient = entry.billableToClient;
  if (entry.assetCode !== undefined) body.AssetCode = entry.assetCode;
  return body;
}

function mapBillOutRate(raw: SPRaw): BillOutRate {
  return {
    id: str(raw.Id) as BillOutRate['id'],
    costGroup: str(raw.CostGroup ?? raw.Title),
    billRate: num(raw.BillRate),
    costRate: num(raw.CostRate),
    effectiveFrom: toISODateString(str(raw.EffectiveFrom, '').split('T')[0] || '1970-01-01'),
    effectiveTo: isoDate(raw.EffectiveTo),
    notes: str(raw.Notes),
    created: raw.Created ? formatISODate(new Date(str(raw.Created))) : toISODateString('1970-01-01'),
    modified: raw.Modified ? formatISODate(new Date(str(raw.Modified))) : toISODateString('1970-01-01'),
  };
}

function buildBillOutRateBody(data: Partial<BillOutRateData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.costGroup !== undefined) { body.Title = data.costGroup; body.CostGroup = data.costGroup; }
  if (data.billRate !== undefined) body.BillRate = data.billRate;
  if (data.costRate !== undefined) body.CostRate = data.costRate;
  if (data.effectiveFrom !== undefined) body.EffectiveFrom = data.effectiveFrom;
  if (data.effectiveTo !== undefined) body.EffectiveTo = data.effectiveTo ?? null;
  if (data.notes !== undefined) body.Notes = data.notes;
  return body;
}

/** Builds the JSON body for audit record POST. */
function buildAuditRecordBody(record: TimeEntryAuditRecord): Record<string, unknown> {
  const title = `${record.oldValue}→${record.newValue} - ${record.periodMonth} - ${record.personName}`;
  return {
    Title: title,
    TimeEntryID: record.timeEntryId,
    PersonName: record.personName,
    PersonEmail: record.personEmail,
    DateWorked: record.dateWorked,
    PeriodMonth: record.periodMonth,
    OldValue: record.oldValue,
    NewValue: record.newValue,
    EditorName: record.editorName,
    EditorEmail: record.editorEmail,
    EditedDate: record.editedDate,
    EditReasonNotes: record.editReasonNotes,
  };
}

// ─── Ordering Mappers & Body Builders ─────────────────────────────────────────

function mapOrder(raw: SPRaw): Order {
  const fyiRaw = str(raw.FYIViewerEmails);
  const fyiViewerEmails = fyiRaw ? fyiRaw.split(',').map(s => s.trim()).filter(Boolean) : [];
  return {
    id: str(raw.Id) as Order['id'],
    spId: num(raw.Id),
    title: str(raw.Title),
    orderRef: str(raw.OrderRef) || `ORD-${String(num(raw.Id)).padStart(4, '0')}`,
    requesterEmail: str(raw.RequesterEmail),
    requesterName: str(raw.RequesterName),
    costAllocation: str(raw.CostAllocation, OrderCostAllocation.Overhead) as OrderCostAllocation,
    projectCode: str(raw.ProjectCode),
    projectName: str(raw.ProjectName),
    supplier: str(raw.Supplier),
    priority: str(raw.Priority, OrderPriority.Standard) as OrderPriority,
    currency: str(raw.Currency, OrderCurrency.GBP) as OrderCurrency,
    totalAmount: num(raw.TotalAmount),
    sterlingEquivalent: raw.SterlingEquivalent != null ? num(raw.SterlingEquivalent) : null,
    orderDate: isoDate(raw.OrderDate),
    requiredByDate: isoDate(raw.RequiredByDate),
    expectedDeliveryDate: isoDate(raw.ExpectedDeliveryDate),
    actualDeliveryDate: isoDate(raw.ActualDeliveryDate),
    status: str(raw.Status, OrderStatus.Draft) as OrderStatus,
    notes: str(raw.Notes),
    poNumber: str(raw.PONumber),
    costCentreName: str(raw.CostCentreName),
    approvedOnBehalfOf: str(raw.ApprovedOnBehalfOf),
    hasChemicalLines: bool(raw.HasChemicalLines),
    isLocked: bool(raw.IsLocked),
    isSelfApproved: bool(raw.IsSelfApproved),
    fyiViewerEmails,
    lineManagerEmail: str(raw.LineManagerEmail),
    lineManagerName: str(raw.LineManagerName),
    cfoApproverEmail: str(raw.CFOApproverEmail),
    escalatedApproverEmail: str(raw.EscalatedApproverEmail),
    escalatedApproverName: str(raw.EscalatedApproverName),
    created: str(raw.Created),
    modified: str(raw.Modified),
  };
}

function mapOrderLineItem(raw: SPRaw): OrderLineItem {
  return {
    id: str(raw.Id) as OrderLineItem['id'],
    orderId: num(raw.OrderId),
    itemDescription: str(raw.ItemDescription ?? raw.Title),
    sku: str(raw.SKU),
    itemLink: str(raw.ItemLink),
    quantity: num(raw.Quantity),
    unitOfMeasure: str(raw.UnitOfMeasure),
    unitPrice: num(raw.UnitPrice),
    subtotal: num(raw.Subtotal),
    isChemical: bool(raw.IsChemical),
    coshhRef: str(raw.COSHHRef),
    lineOrder: num(raw.LineOrder),
  };
}

function mapOrderApprovalLog(raw: SPRaw): OrderApprovalLog {
  return {
    id: str(raw.Id) as OrderApprovalLog['id'],
    orderId: num(raw.OrderId),
    approvalStage: str(raw.ApprovalStage, ApprovalStage.LineManager) as ApprovalStage,
    approverName: str(raw.ApproverName),
    approverEmail: str(raw.ApproverEmail),
    decision: str(raw.Decision, ApprovalDecision.Approved) as ApprovalDecision,
    note: str(raw.Note),
    onBehalfOf: str(raw.OnBehalfOf),
    approvalDate: str(raw.ApprovalDate),
    attachmentRef: str(raw.AttachmentRef),
  };
}

function mapOrderComment(raw: SPRaw): OrderComment {
  return {
    id: str(raw.Id) as OrderComment['id'],
    orderId: num(raw.OrderId),
    authorName: str(raw.AuthorName),
    authorEmail: str(raw.AuthorEmail),
    commentText: str(raw.CommentText),
    commentDate: str(raw.CommentDate),
  };
}

function mapCOSHHEntry(raw: SPRaw): COSHHEntry {
  return {
    id: str(raw.Id) as COSHHEntry['id'],
    coshhRef: str(raw.Title),
    chemicalName: str(raw.ChemicalName),
    hazardType: str(raw.HazardType),
    description: str(raw.Description),
  };
}

function buildOrderBody(data: Partial<OrderData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.title !== undefined) body.Title = data.title;
  if (data.orderRef !== undefined) body.OrderRef = data.orderRef;
  if (data.requesterEmail !== undefined) body.RequesterEmail = data.requesterEmail;
  if (data.requesterName !== undefined) body.RequesterName = data.requesterName;
  if (data.costAllocation !== undefined) body.CostAllocation = data.costAllocation;
  if (data.projectCode !== undefined) body.ProjectCode = data.projectCode;
  if (data.projectName !== undefined) body.ProjectName = data.projectName;
  if (data.supplier !== undefined) body.Supplier = data.supplier;
  if (data.priority !== undefined) body.Priority = data.priority;
  if (data.currency !== undefined) body.Currency = data.currency;
  if (data.totalAmount !== undefined) body.TotalAmount = data.totalAmount;
  if (data.sterlingEquivalent !== undefined) body.SterlingEquivalent = data.sterlingEquivalent;
  if (data.orderDate !== undefined) body.OrderDate = data.orderDate;
  if (data.requiredByDate !== undefined) body.RequiredByDate = data.requiredByDate;
  if (data.expectedDeliveryDate !== undefined) body.ExpectedDeliveryDate = data.expectedDeliveryDate;
  if (data.actualDeliveryDate !== undefined) body.ActualDeliveryDate = data.actualDeliveryDate;
  if (data.status !== undefined) body.Status = data.status;
  if (data.notes !== undefined) body.Notes = data.notes;
  if (data.poNumber !== undefined) body.PONumber = data.poNumber;
  if (data.costCentreName !== undefined) body.CostCentreName = data.costCentreName;
  if (data.approvedOnBehalfOf !== undefined) body.ApprovedOnBehalfOf = data.approvedOnBehalfOf;
  if (data.hasChemicalLines !== undefined) body.HasChemicalLines = data.hasChemicalLines;
  if (data.isLocked !== undefined) body.IsLocked = data.isLocked;
  if (data.isSelfApproved !== undefined) body.IsSelfApproved = data.isSelfApproved;
  if (data.fyiViewerEmails !== undefined) body.FYIViewerEmails = data.fyiViewerEmails?.join(',') ?? '';
  if (data.lineManagerEmail !== undefined) body.LineManagerEmail = data.lineManagerEmail;
  if (data.lineManagerName !== undefined) body.LineManagerName = data.lineManagerName;
  if (data.cfoApproverEmail !== undefined) body.CFOApproverEmail = data.cfoApproverEmail;
  if (data.escalatedApproverEmail !== undefined) body.EscalatedApproverEmail = data.escalatedApproverEmail;
  if (data.escalatedApproverName !== undefined) body.EscalatedApproverName = data.escalatedApproverName;
  return body;
}

function buildLineItemBody(data: Partial<NewLineItemData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.itemDescription !== undefined) { body.Title = data.itemDescription.slice(0, 255); body.ItemDescription = data.itemDescription; }
  if (data.sku !== undefined) body.SKU = data.sku;
  if (data.itemLink !== undefined) {
    let link = data.itemLink.trim();
    if (link) {
      try {
        const u = new URL(/^https?:\/\//i.test(link) ? link : `https://${link}`);
        link = u.origin + u.pathname;
      } catch { /* keep as-is if not a valid URL */ }
    }
    body.ItemLink = link.slice(0, 255);
  }
  if (data.orderId !== undefined) body.OrderId = data.orderId;
  if (data.quantity !== undefined) body.Quantity = data.quantity;
  if (data.unitOfMeasure !== undefined) body.UnitOfMeasure = data.unitOfMeasure;
  if (data.unitPrice !== undefined) body.UnitPrice = data.unitPrice;
  if (data.subtotal !== undefined) body.Subtotal = data.subtotal;
  if (data.isChemical !== undefined) body.IsChemical = data.isChemical;
  if (data.coshhRef !== undefined) body.COSHHRef = data.coshhRef;
  if (data.lineOrder !== undefined) body.LineOrder = data.lineOrder;
  return body;
}

function buildApprovalBody(data: NewApprovalData): Record<string, unknown> {
  return {
    Title: `Order #${data.orderId} – ${data.approvalStage} – ${data.decision}`,
    OrderId: data.orderId,
    ApprovalStage: data.approvalStage,
    ApproverName: data.approverName,
    ApproverEmail: data.approverEmail,
    Decision: data.decision,
    Note: data.note ?? '',
    OnBehalfOf: data.onBehalfOf ?? '',
    ApprovalDate: new Date().toISOString(),
    AttachmentRef: data.attachmentRef ?? '',
  };
}

function buildCommentBody(data: NewCommentData, now: string): Record<string, unknown> {
  return {
    Title: `Comment on Order #${data.orderId}`,
    OrderId: data.orderId,
    AuthorName: data.authorName,
    AuthorEmail: data.authorEmail,
    CommentText: data.commentText,
    CommentDate: now,
  };
}
